import { useDebounce } from '@uidotdev/usehooks';
import { useEffect, useState } from 'react';

import UpdateHandlers from '@/features/Checkout/types/updateHandlers.type';
import viewShippingProfiles from '@/shared/api/shipping/profiles/viewShippingProfiles';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import DialogName from '@/shared/types/dialogName.enum';
import ShippingProfile from '@/shared/types/shipping/shippingProfile.type';

type Args = UpdateHandlers<unknown>;

const useHandleShipToCustomer = ({ onUpdateDialogState }: Args) => {
  const redirectLogin = useRedirectLogin();

  const [shippingProfiles, setShippingProfiles] = useState<
    ShippingProfile[] | null
  >(null);

  const [selectedShippingProfileId, setSelectedShippingProfileId] =
    useState('');

  const [selectedZoneId, setSelectedZoneId] = useState('');

  const [shippingPrice, setShippingPrice] = useState('');

  const [isLoading, setIsLoading] = useState(false);

  const [shippingProfileSearchValue, setShippingProfileSearchValue] =
    useState('');

  const shippingProfileSearchValueDebounced = useDebounce(
    shippingProfileSearchValue,
    500,
  );

  const onSelectShippingProfile = (value: string) => {
    setSelectedShippingProfileId(value);
  };

  const onSelectShippingProfileZone = (value: string) => {
    setSelectedZoneId(value);
  };

  const openShipToCustomerDialog = () => {
    onUpdateDialogState(DialogName.ShipToCustomer, {
      open: true,
      data: shippingProfiles,
    });
  };

  useEffect(() => {
    const selectedProfileItem = shippingProfiles?.find(
      (item) => item.shipping_profile_id === selectedShippingProfileId,
    );

    if (selectedProfileItem && selectedProfileItem.zones.length > 0) {
      onSelectShippingProfileZone(
        selectedProfileItem.zones[0].shipping_profile_zone_id,
      );
      setShippingPrice(selectedProfileItem.zones[0].shipping_rate);
    }
  }, [selectedShippingProfileId, shippingProfiles]);

  useEffect(() => {
    const fetchShippingProfiles = async () => {
      setIsLoading(true);

      // Todo: use the columns below to perform search over shipping profiles functionality.
      // const requiredFields: Partial<keyof ShippingProfile>[] = [
      //   'category',
      //   'display_name',
      //   'in_account_id',
      //   'in_tenant_id',
      //   'includes_return_label',
      //   'package_type',
      //   'packaging_properties',
      //   'shipping_profile_description',
      //   'payment_method_id',
      //   'shipping_method_id',
      //   'shipping_profile_id',
      //   'shipping_provider_id',
      //   'shipping_provider_key',
      //   'signature_requirement',
      //   'ts_created',
      //   'ts_modified',
      // ];

      // const initialFilters = requiredFields.map((key, index) => ({
      //   [key]: {
      //     cop: ['ilike'],
      //     value: shippingProfileSearchValueDebounced,
      //     op: index > 0 ? 'or' : undefined,
      //   },
      // }));

      const response = await viewShippingProfiles({
        body: { search: [] },
        onUnauthorized: redirectLogin,
      });

      setIsLoading(false);

      if (response.success) {
        setShippingProfiles(response.data);
        setSelectedShippingProfileId(response.data[0].shipping_profile_id);
        setShippingPrice(response.data[0].zones[0].shipping_rate);
      }
    };

    void fetchShippingProfiles();
  }, [shippingProfileSearchValueDebounced, redirectLogin]);

  return {
    shippingProfiles,
    isLoading,
    selectedShippingProfileId,
    selectedZoneId,
    shippingPrice,
    setShippingPrice,
    setShippingProfileSearchValue,
    onSelectShippingProfile,
    onSelectShippingProfileZone,
    trigger: openShipToCustomerDialog,
  };
};

export default useHandleShipToCustomer;
